import { computed, signal } from '@preact/signals';
import { loadLogList, type LoadedLogList } from '../../core/loglist/fetch';
import { customLogToDescriptor, loadCustomLogs, saveCustomLogs, type CustomLog } from '../../core/loglist/custom';
import { LogRegistry } from '../../core/loglist/registry';
import { mergeDescriptors } from '../../core/loglist/schema';
import { BUILTIN_SOURCES, type ListSource } from '../../core/loglist/sources';
import type { LogDescriptor } from '../../core/log/client';
import { settings } from './settings';
import { storage } from './storage';

export interface SourceResult {
  source: ListSource;
  list?: LoadedLogList;
  error?: string;
}

export interface RegistryState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  results: SourceResult[];
}

export const registryState = signal<RegistryState>({ status: 'idle', results: [] });
export const customLogs = signal<CustomLog[]>(loadCustomLogs(storage));
const customDescriptors = signal<LogDescriptor[]>([]);

async function refreshCustomDescriptors(): Promise<void> {
  const out: LogDescriptor[] = [];
  for (const c of customLogs.value) {
    try {
      out.push(await customLogToDescriptor(c));
    } catch {
      /* skip malformed */
    }
  }
  customDescriptors.value = out;
}
void refreshCustomDescriptors();

/** Merged registry over every loaded source plus the user's custom logs. */
export const registry = computed(() => {
  const lists = registryState.value.results.map((r) => r.list?.descriptors ?? []);
  return new LogRegistry([...mergeDescriptors(lists), ...customDescriptors.value]);
});

/** The list sources enabled in Settings, in program priority order. */
export function enabledSources(): ListSource[] {
  const s = settings.value;
  const out = BUILTIN_SOURCES.filter((b) => s.sources.includes(b.id));
  const custom = s.customListUrl.trim();
  if (custom) out.push({ id: 'custom', program: 'custom', label: 'Custom list', description: custom, url: custom });
  return out;
}

let loadedKey: string | undefined;

export async function ensureRegistry(force = false): Promise<void> {
  const sources = enabledSources();
  const key = sources.map((s) => s.url).join('|');
  if (!force && loadedKey === key && registryState.value.status === 'ready') return;
  loadedKey = key;
  registryState.value = { status: 'loading', results: registryState.value.results.filter((r) => sources.some((s) => s.id === r.source.id)) };
  const results = await Promise.all(
    sources.map(async (source): Promise<SourceResult> => {
      try {
        return { source, list: await loadLogList(source.url, { cache: storage, force, program: source.program }) };
      } catch (e) {
        return { source, error: e instanceof Error ? e.message : String(e) };
      }
    }),
  );
  registryState.value = { status: results.some((r) => r.list) ? 'ready' : 'error', results };
}

export async function addCustomLog(c: CustomLog): Promise<LogDescriptor> {
  const d = await customLogToDescriptor(c); // validates
  const next = [...customLogs.value.filter((x) => x.url !== c.url), c];
  customLogs.value = next;
  saveCustomLogs(storage, next);
  await refreshCustomDescriptors();
  return d;
}

export function removeCustomLog(url: string): void {
  const next = customLogs.value.filter((x) => x.url !== url);
  customLogs.value = next;
  saveCustomLogs(storage, next);
  void refreshCustomDescriptors();
}
